import { APIParameters, APIResult } from "common";
import { useCallback, useEffect, useState } from "react";
import { APICall } from "../api/api";
import { useAPIQuery } from "../api/useAPIQuery";

export const useInfiniteAPIQuery = <
  T extends keyof APIParameters,
  Item extends { [k: string]: any },
  ItemIdField extends keyof Item,
  ItemSortField extends keyof Item
>({
  apiCall,
  transformer,
  queryParams,
  itemsPerPage,
  idField,
  sortField,
  sortOrder,
}: {
  apiCall: APICall<T>;
  // Transforms the result into a list of items
  transformer: (result: APIResult[T]) => Item[];
  queryParams: APIParameters[T];
  itemsPerPage: number;
  idField: ItemIdField;
  sortField: ItemSortField;
  sortOrder: "DESC" | "ASC";
}) => {
  const [items, setItems] = useState<Item[]>([]);
  const { data, state, error, query } = useAPIQuery({
    apiCall,
  });
  const [offset, setOffset] = useState(0);
  const [isEnd, setIsEnd] = useState(false);

  /*
   * Loads more items
   */
  const onBottomReached = useCallback(() => {
    if (isEnd || state === "loading") {
      return;
    }

    // Update offset
    setOffset((offset) => {
      // Queries for the given page
      query({
        ...queryParams,
        offset,
        limit: itemsPerPage,
      });
      return offset + itemsPerPage;
    });
  }, [isEnd, itemsPerPage, query, queryParams, state]);

  /*
   * Update items whenever queried
   */
  useEffect(() => {
    if (!data) {
      return;
    }
    const newItems = transformer(data);

    // End reached
    if (newItems.length === 0) {
      setIsEnd(true);
      return;
    }

    // Merge the items with the new items
    setItems((items) => {
      const mergedItems: Item[] = Object.values(
        Object.fromEntries([
          ...items.map((item) => [item.id, item]),
          ...newItems.map((item) => [item.id, item]),
        ])
      );

      // Sort the items
      mergedItems.sort((a, b) => {
        if (a[sortField] < b[sortField]) {
          return sortOrder === "ASC" ? -1 : 1;
        }
        return sortOrder === "ASC" ? 1 : -1;
      });

      // No change so return old items
      if (items.length === mergedItems.length) {
        return items;
      }

      // Return
      return mergedItems;
    });
  }, [data, sortField, sortOrder, transformer]);

  return {
    items,
    onBottomReached,
    isEnd,
    error,
    offset,
  };
};
